import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "Earnings per share (EPS) is a company's net profit divided by its number of outstanding shares. It tells you how much profit each share represents over a given period. EPS of $5 means the company earned $5 of profit for every share. EPS feeds directly into the P/E ratio and is the headline number every quarterly earnings report leads with.";

const FAQS = [
  { q: 'What is the difference between basic EPS and diluted EPS?', a: 'Basic EPS uses current shares outstanding. Diluted EPS adds shares that could be created from stock options, convertible bonds, or warrants. Diluted EPS is more conservative and is the version analysts usually quote.' },
  { q: 'Is a higher EPS always better?', a: 'Higher is generally better, but you need to compare it to share price (via the P/E ratio) and to the company\'s own EPS history. A $10 EPS at a $1,000 share price is no better than a $1 EPS at a $100 share price.' },
  { q: 'Can EPS be negative?', a: 'Yes. If a company posts a net loss, EPS is negative. This is common for early-stage growth companies still investing heavily.' },
  { q: 'How often is EPS reported?', a: 'Public companies report EPS every quarter as part of their earnings release. Annual EPS is the sum of the four quarterly figures.' },
  { q: 'Why do share buybacks raise EPS?', a: 'Buybacks reduce shares outstanding, the denominator in the EPS formula. Even with flat profits, fewer shares mean higher EPS — which is one reason buybacks are so popular with management.' },
];

export default function WhatIsEpsPage() {
  return (
    <BlogArticleLayout
      slug="what-is-eps"
      title="What Is EPS (Earnings Per Share)?"
      description="EPS explained simply: formula, basic vs diluted EPS, what a 'good' EPS looks like, and why buybacks inflate it."
      keywords="EPS, earnings per share, what is EPS, EPS formula, basic vs diluted EPS, how to calculate EPS, good EPS, EPS explained"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['what-is-pe-ratio', 'revenue-vs-earnings', 'how-to-value-a-stock']}
    >
      <section>
        <h2>What is earnings per share?</h2>
        <p>
          EPS slices a company's total profit into per-share terms so investors can compare profitability across companies of any size.
          The formula is: <strong>EPS = (Net Income − Preferred Dividends) ÷ Shares Outstanding</strong>.
        </p>
        <p>
          A bakery that earned $1 million in profit with 100,000 shares outstanding has EPS of $10. A megacap that earned $50 billion with 10 billion shares outstanding has EPS of $5.
          The bakery looks more profitable per share — but that comparison only matters once you also factor in share price.
        </p>
      </section>

      <section>
        <h2>How do you calculate EPS?</h2>
        <p>
          Pull two numbers off the income statement:
        </p>
        <ul>
          <li><strong>Net income</strong> — bottom-line profit after all expenses, interest, and taxes.</li>
          <li><strong>Shares outstanding</strong> — usually the weighted average over the reporting period.</li>
        </ul>
        <p>
          Worked example: Apple posted ~$94B net income last year with ~15.4B average shares outstanding. EPS ≈ 94 ÷ 15.4 = <strong>$6.10</strong>.
          That figure is what every "Apple beats EPS estimate" headline is comparing against analyst forecasts.
        </p>
      </section>

      <section>
        <h2>What's the difference between basic and diluted EPS?</h2>
        <p>
          Most companies report two versions of EPS:
        </p>
        <ul>
          <li><strong>Basic EPS</strong> uses only shares currently outstanding.</li>
          <li><strong>Diluted EPS</strong> also includes shares that <em>could</em> be created from stock options, restricted stock units, convertible bonds, or warrants.</li>
        </ul>
        <p>
          Diluted EPS is always equal to or lower than basic EPS. Tech companies that hand out lots of stock options usually have a meaningful gap between the two.
          Most analysts and most P/E calculations use the diluted figure.
        </p>
      </section>

      <section>
        <h2>What's a "good" EPS?</h2>
        <p>
          EPS in isolation is meaningless — it depends on share price. The right way to use EPS is:
        </p>
        <ul>
          <li><strong>Trend over time.</strong> Is EPS growing year over year? That is what drives long-term stock returns.</li>
          <li><strong>Versus expectations.</strong> Beating analyst EPS estimates usually pushes the stock up; missing pushes it down.</li>
          <li><strong>Combined with price.</strong> Divide share price by EPS to get the <Link to="/blog/what-is-pe-ratio">P/E ratio</Link> and you get a real valuation signal.</li>
        </ul>
      </section>

      <section>
        <h2>Why do buybacks distort EPS?</h2>
        <p>
          When a company buys back its own shares, shares outstanding drops. The denominator in the EPS formula shrinks, so EPS rises even if total profit is flat.
          Many CEOs are rewarded based on EPS growth, which gives them a strong incentive to run buybacks.
          When you see EPS jumping, check whether profit actually grew or whether the share count just shrank.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
